import React, { useState } from "react";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import { SlidersHorizontal } from "lucide-react";
import clsx from "clsx";

const fleetColor = "#1a5276"; // Single color for fleet average

const EnginePowerGraph = ({
  data = [],
  operationalRanges = {},
  isLoading = false,
}) => {
  // console.log("data", data);
  const [isOpen, setIsOpen] = useState(true);

  // Use props if available, otherwise use mock data
  const actualFleetData = data;
  // const actualFleetData = filteredData;

  const calculateFleetAverages = () => {
    // Get all unique speed points across all vessels
    const allSpeeds = Array.from(
      new Set(
        actualFleetData.flatMap((vessel) =>
          vessel.powerCurve.map((point) => point.speed || 0)
        )
      )
    ).sort((a, b) => a - b);

    return allSpeeds.map((speed) => {
      // Find all vessels that have this speed point
      const vesselsWithSpeed = actualFleetData
        .map((vessel) => {
          const point = vessel.powerCurve.find((p) => p.speed === speed);
          return point ? { name: vessel.vessel, power: point.power } : null;
        })
        .filter((entry) => entry !== null);

      // Calculate average power at this speed
      const avgPower =
        vesselsWithSpeed.length > 0
          ? vesselsWithSpeed.reduce((sum, v) => sum + v.power, 0) /
          vesselsWithSpeed.length
          : 0;

      return {
        speed,
        power: Math.round(avgPower),
        vessels: vesselsWithSpeed.map((v) => v.name), // vessel names list
      };
    });
  };

  const fleetAverages = calculateFleetAverages();
  // console.log("fleet_averages", fleetAverages);

  let calculatedRanges = {};
  if (fleetAverages.length > 0) {
    const speeds = fleetAverages.map((d) => d.speed);
    const minSpeed = Math.min(...speeds);
    const maxSpeed = Math.max(...speeds);

    calculatedRanges = {
      eco: {
        min: minSpeed,
        max: minSpeed + (maxSpeed - minSpeed) * 0.3,
      },
      highSpeed: {
        min: minSpeed + (maxSpeed - minSpeed) * 0.6,
        max: maxSpeed,
      },
    };
  }

  const actualOperationalRanges =
    Object.keys(operationalRanges).length > 0
      ? operationalRanges
      : calculatedRanges;

  console.log("actualOperationalRanges", actualOperationalRanges);

  // Prepare series data for fleet average
  const series = [
    {
      name: "Fleet Average",
      type: "line",
      data: fleetAverages.map((item) => [item.speed, item.power, item.vessels]),
      color: fleetColor,
      lineWidth: 4,
      marker: {
        enabled: true,
        radius: 5,
        symbol: "circle",
        fillColor: "#ffffff",
        lineWidth: 2,
        lineColor: fleetColor,
      },
      zIndex: 5,
    },
  ];

  // Add min/max range as an area series
  if (actualFleetData.length > 1) {
    const minMaxData = fleetAverages.map((avg) => {
      const powersAtSpeed = actualFleetData
        .map((vessel) => {
          const point = vessel.powerCurve.find((p) => p.speed === avg.speed);
          return point ? point.power : null;
        })
        .filter((p) => p !== null);

      return {
        speed: avg.speed,
        min: Math.min(...powersAtSpeed),
        max: Math.max(...powersAtSpeed),
      };
    });
  }

  const options = {
    chart: {
      type: "line",
      backgroundColor: "transparent",
      height: 460,
      spacingBottom: 15,
    },
    title: {
      text: null,
      align: "left",
      style: {
        color: "#1a325d",
        fontSize: "16px",
        fontWeight: "600",
      },
    },
    xAxis: {
      title: {
        text: "Speed (Knots)",
        style: {
          color: "#374151",
          fontWeight: "500",
        },
      },
      labels: {
        style: {
          color: "#6b7280",
          fontSize: "12px",
        },
      },
      plotLines: [
        { color: "#d1d5db", width: 1, value: 10, dashStyle: "ShortDash" },
        { color: "#d1d5db", width: 1, value: 14, dashStyle: "ShortDash" },
        { color: "#d1d5db", width: 1, value: 18, dashStyle: "ShortDash" },
      ],
      plotBands: [
        {
          from: actualOperationalRanges?.eco?.min,
          to: actualOperationalRanges?.eco?.max,
          color: "rgba(37, 99, 235, 0.08)",
          label: {
            text: "Eco Range",
            style: { color: "#2563eb", fontWeight: "bold" },
          },
        },
        {
          from: actualOperationalRanges?.highSpeed?.min,
          to: actualOperationalRanges?.highSpeed?.max,
          color: "rgba(255, 237, 213, 0.5)",
          label: {
            text: "High-Speed Zone",
            style: { color: "#ea580c", fontWeight: "bold" },
          },
        },
      ],
    },
    yAxis: {
      title: {
        text: "Power (BHP)",
        style: {
          color: "#374151",
          fontWeight: "500",
        },
      },
      labels: {
        style: {
          color: "#6b7280",
          fontSize: "12px",
        },
      },
      gridLineColor: "#f3f4f6",
      minPadding: 0,
      maxPadding: 0.05,
      tickAmount: 6,
    },
    tooltip: {
      formatter: function () {
        const point = fleetAverages.find((p) => p.speed === this.x);
        const vesselNames = point?.vessels?.join(", ") || "N/A";
        return `<b>${vesselNames}</b><br/>
                <b>Speed:</b> ${this.x} knots<br/>
                <b>Power:</b> ${this.y} BHP`;
      },
      backgroundColor: "#ffffff",
      borderColor: "#e5e7eb",
      borderRadius: 8,
      shadow: true,
      style: { color: "#111827" },
    },
    series: series,
    credits: { enabled: false },
    legend: { enabled: false },
    plotOptions: {
      series: {
        states: {
          hover: {
            halo: {
              size: 8,
              opacity: 0.1,
            },
          },
        },
      },
      arearange: {
        tooltip: {
          pointFormat: "<b>Range:</b> {point.low} - {point.high} BHP",
        },
      },
    },
  };

  if (isLoading) {
    return (
      <div className="w-full h-[500px] rounded-3xl shadow-2xl flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <div className="w-8 h-8 border-4 border-blue-500/20 border-t-blue-500 rounded-full animate-spin"></div>
          <p className="text-gray-600 font-medium">Loading engine data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full transition-all duration-300 overflow-hidden">
      {/* Chart */}
      <div
        className={clsx(
          "transition-all duration-500 overflow-hidden px-4 bg-white",
          isOpen ? "pt-4 max-h-[520px]" : "py-0 max-h-0"
        )}
      >
        <HighchartsReact highcharts={Highcharts} options={options} />
      </div>
    </div>
  );
};

export default EnginePowerGraph;
